import { AxiosError, AxiosResponse } from "axios";
import {
  IBillingProfileScreenPanelsAssignmentResponsePayload,
  ITaxProfileListModel,
} from "../models/taxProfile";
import { IGetListRequest, IResponse, IServerError } from "../models";

import { IDropDownListResponse } from "../models/common/IDropDownListResponse";
import { ITaxProfileSetupRequest } from "../models/taxProfileSetup";
import http from "../common/http-commons";

const API_BASE_RESOURCE_URL = "/v1/tax-profiles/";

export const TaxProfileService = {
  getList: async (
    params: IGetListRequest,
    navigateToLogout: (path: string) => void,
  ): Promise<ITaxProfileListModel[] | IServerError | IResponse> => {
    try {
      const response: AxiosResponse<ITaxProfileListModel[]> = await http.get<
        ITaxProfileListModel[]
      >(
        `/v1/tax-profiles/?order_by=${params.order_by}&order_direction=${params.order_direction}&page=${params.page}&size=${params.size}&search_query=${params.search_query}`,
      );

      if (response.status === 200) return response.data;

      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      if (axiosError && axiosError.status === 401) {
        navigateToLogout("/logout");
        return {
          status: false,
          message: "UnAuthrized",
        };
      } else {
        return {
          status: false,
          message: errorMessage,
        };
      }
    }
  },

  delTaxProfile: async (
    requestPayload: any,
  ): Promise<string | IServerError | IResponse> => {
    try {
      const response: AxiosResponse<string> = await http.delete<string>(
        `/v1/tax-profiles/${requestPayload.taxProfileId}/`,
      );

      if (response.status === 200) {
        return "Tax Profile has been deleted successfully";
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }

      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      if (axiosError && axiosError.status === 401) {
        requestPayload.navigateToLogout("/logout");
        return {
          status: false,
          message: "UnAuthrized",
        };
      } else {
        return {
          status: false,
          message: errorMessage,
        };
      }
    }
  },

  delBulkTaxProfile: async (
    requestPayload: any,
  ): Promise<string | IServerError | IResponse> => {
    try {
      const response = await http.delete<string>(`/v1/tax-profiles/`, {
        data: requestPayload.bulkTaxProfileId,
      });
      if (response.status === 200) {
        return response.data;
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }

      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      if (axiosError && axiosError.status === 401) {
        requestPayload.navigateToLogout("/logout");
        return {
          status: false,
          message: "UnAuthrized",
        };
      } else {
        return {
          status: false,
          message: errorMessage,
        };
      }
    }
  },

  create: async (requestPayload: ITaxProfileSetupRequest): Promise<any> => {
    if (requestPayload.id) {
      const response: AxiosResponse<ITaxProfileSetupRequest> =
        await http.put<ITaxProfileSetupRequest>(
          `${API_BASE_RESOURCE_URL}${requestPayload.id}/`,
          requestPayload,
        );

      if (response.status === 200)
        return {
          success: true,
          message: "Record has been updated successfully",
          data: response.data,
        };

      return {
        success: false,
        message: "Something went wrong please try again later.",
      };
    }

    const response: AxiosResponse<ITaxProfileSetupRequest> =
      await http.post<ITaxProfileSetupRequest>(
        API_BASE_RESOURCE_URL,
        requestPayload,
      );

    if (response.status === 201)
      return {
        success: true,
        message: "Record has been added successfully",
        data: response.data,
      };

    return { success: false, data: response.data };
  },
  getById: async (
    id: string | undefined,
  ): Promise<ITaxProfileSetupRequest | IServerError | IResponse | any> => {
    if (id) {
      const response: AxiosResponse<ITaxProfileSetupRequest> =
        await http.get<ITaxProfileSetupRequest>(`/v1/tax-profiles/${id}/`);

      if (response.status === 200) {
        return response.data;
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }

      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    }

    return null;
  },
  getProfilesDropDownList: async (): Promise<
    IDropDownListResponse[] | IServerError | IResponse
  > => {
    const response: AxiosResponse<IDropDownListResponse[]> = await http.get<
      IDropDownListResponse[]
    >("/v1/billing-profile/ddl-list");

    if (response.status === 200) {
      return response.data;
    } else if (response.status === 500) {
      return {
        status: false,
        message: "Something went wrong.",
        error: true,
      };
    }

    return {
      status: false,
      message: "Something went wrong please try again later.",
    };
  },
  getAccountantsDropDownList: async (): Promise<
    IDropDownListResponse[] | IServerError | IResponse | any
  > => {
    const response: AxiosResponse<IDropDownListResponse[]> = await http.get<
      IDropDownListResponse[]
    >("/v1/accountants/ddl");

    if (response.status === 200) {
      return response.data;
    } else if (response.status === 500) {
      return {
        status: false,
        message: "Something went wrong.",
        error: true,
      };
    }

    return {
      status: false,
      message: "Something went wrong please try again later.",
    };
    // return[
    //     { label: "Accountant 1", value: "1" },
    //     { label: "Accountant 2", value: "2" },
    //   ]
  },
  getPanelsAssignedToScreen: async (
    accountantId: number | string,
    billingProfileId: number | string,
  ): Promise<
    | IBillingProfileScreenPanelsAssignmentResponsePayload
    | IServerError
    | IResponse
    | null
  > => {
    const response: AxiosResponse<IBillingProfileScreenPanelsAssignmentResponsePayload> =
      await http.get<IBillingProfileScreenPanelsAssignmentResponsePayload>(
        `/v1/billing-profile-association/get-assiign-unassign-client?billing_profile_id=${billingProfileId}&accountant_id=${accountantId}`,
      );

    if (response.status === 200) {
      return response.data;
    } else if (response.status === 500) {
      return {
        status: false,
        message: "Something went wrong.",
        error: true,
      };
    }

    return {
      status: false,
      message: "Something went wrong please try again later.",
    };
  },

  createProfileAssociation: async (
    requestPayload: any,
  ): Promise<any | IServerError | IResponse> => {
    let response: AxiosResponse<any>;

    if (requestPayload.billingAssociationId == undefined) {
      const transformedPayload = {
        billingProfileId: requestPayload.profileId,
        clients: [requestPayload.clientId],
      };

      response = await http.post<any>(
        `/v1/billing-profile-association/`,
        transformedPayload,
      );
    } else {
      response = await http.put<any>(
        `/v1/billing-profile-association/${requestPayload.profileId}/assign-clients/?client_id=${requestPayload.clientId}&association_flag=${requestPayload.flag}`,
      );
    }

    if (response.status === 201) {
      return response;
    } else if (response.status === 200) {
      return response;
    } else if (response.status === 400) {
      return {
        status: false,
        message: "400 Bad Request.",
        error: true,
      };
    } else if (response.status === 500) {
      return {
        status: false,
        message: "Something went wrong.",
        error: true,
      };
    }

    return {
      status: false,
      message: "Something went wrong please try again later.",
    };
  },
  archiveTaxProfile: async (
    archivePayload: any,
  ): Promise<string | IServerError | IResponse | any> => {
    // return new Promise(resolve => setTimeout(() => resolve("Flex Field has been deleted successfully"), 1000));
    try {
      const response: AxiosResponse<string> = await http.post<string>(
        `/v1/taxProfile/${archivePayload.taxProfileId}/`,
      );

      if (response.status === 201) {
        return "Tax Profile has been archived successfully";
      } else if (response.status === 200) {
        return "Tax Profile has been archived successfully";
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }

      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      if (axiosError && axiosError.status === 401) {
        archivePayload.navigateToLogout("/logout");
        return {
          status: false,
          message: "UnAuthrized",
        };
      } else {
        return {
          status: false,
          message: errorMessage,
        };
      }
    }
  },
};
